import {
  ENUM_LABELS,
  type ReagentItemStock,
  type ReagentLot,
  type StockMovementKind,
  type StockMovementRow,
} from '#shared/types';
import { Badge, type BadgeTone } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { formatDate, formatDateTime, formatNumber, dash } from '#client/lib/format';

/**
 * The inventory tables' columns.
 *
 * At module scope, like every other column array in the app, so they are not
 * reallocated on every render. The lot table's cost header names the reagent's
 * unit, so that one is built per reagent.
 */

function signedClass(quantity: number): string {
  return quantity < 0 ? 'c-inventory__negative tnum' : 'tnum';
}

export const reagentStockColumns: Column<ReagentItemStock>[] = [
  {
    key: 'name',
    header: 'Reagent',
    frozen: true,
    render: (row) => (
      <span>
        <strong>{row.name}</strong>
        <span className="c-inventory__code ident">{row.code}</span>
      </span>
    ),
  },
  {
    key: 'department',
    header: 'Department',
    width: '160px',
    render: (row) => row.department_name,
  },
  {
    key: 'on_hand',
    header: 'On hand',
    width: '140px',
    align: 'end',
    render: (row) => (
      // Negative is real and is shown as such. Their sheet shows -14; clamping
      // it to zero destroys the only evidence of the discrepancy.
      <span className={signedClass(row.on_hand_base)}>
        {formatNumber(row.on_hand_base)} {ENUM_LABELS.stockUnit[row.base_unit]}
      </span>
    ),
  },
  {
    key: 'received',
    header: 'Received',
    width: '120px',
    align: 'end',
    render: (row) => <span className="tnum text-fg-muted">{formatNumber(row.received_base)}</span>,
  },
  {
    key: 'runs',
    header: 'Runs left',
    width: '110px',
    align: 'end',
    render: (row) =>
      row.runs_available === null ? (
        <span className="text-fg-muted">—</span>
      ) : (
        <span className="tnum">{row.runs_available}</span>
      ),
  },
  {
    key: 'notice',
    header: 'Notice',
    render: (row) =>
      row.notice === null ? (
        <span className="text-fg-muted">—</span>
      ) : (
        <Badge tone={row.on_hand_base <= 0 ? 'critical' : 'warning'}>{row.notice}</Badge>
      ),
  },
];

function LotExpiry({ lot }: { lot: ReagentLot }) {
  if (lot.expiry_at === null) return <span className="text-fg-muted">Does not expire</span>;
  if (lot.is_expired) return <Badge tone="critical">Expired {formatDate(lot.expiry_at)}</Badge>;
  return formatDate(lot.expiry_at);
}

function LotStatusBadge({ lot }: { lot: ReagentLot }) {
  if (lot.status === 'active') return <Badge tone="success">Active</Badge>;
  return (
    <Badge tone={lot.status === 'discarded' ? 'neutral' : 'warning'}>
      {ENUM_LABELS.lotStatus[lot.status]}
    </Badge>
  );
}

/** One reagent's lots. `unitLabel` is the reagent's counting unit, for the cost header. */
export function reagentLotColumns(unitLabel: string): Column<ReagentLot>[] {
  return [
    {
      key: 'lot_no',
      header: 'Lot',
      frozen: true,
      render: (row) => <span className="ident">{row.lot_no}</span>,
    },
    {
      key: 'expiry',
      header: 'Expiry',
      width: '150px',
      render: (row) => <LotExpiry lot={row} />,
    },
    {
      key: 'on_hand',
      header: 'On hand',
      width: '140px',
      align: 'end',
      render: (row) => (
        <span className={signedClass(row.on_hand_base)}>{formatNumber(row.on_hand_base)}</span>
      ),
    },
    {
      key: 'received',
      header: 'Received',
      width: '120px',
      align: 'end',
      render: (row) => (
        <span className="tnum text-fg-muted">{formatNumber(row.received_base)}</span>
      ),
    },
    {
      key: 'unit_cost',
      header: `Cost / ${unitLabel}`,
      width: '130px',
      align: 'end',
      render: (row) => (
        <span className="tnum">Rs {formatNumber(row.unit_cost, { maxFractionDigits: 6 })}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      render: (row) => <LotStatusBadge lot={row} />,
    },
    { key: 'supplier', header: 'Supplier', render: (row) => dash(row.supplier_name) },
  ];
}

/**
 * Tone by direction, not by kind.
 *
 * What a reader wants at a glance is "did stock go in or out", and colouring
 * six kinds six ways makes that harder rather than easier. `wastage` is the one
 * exception: it is stock that was paid for and never used, which is a different
 * fact from stock that did a job.
 */
const MOVEMENT_TONES: Record<StockMovementKind, BadgeTone> = {
  receipt: 'success',
  opening_balance: 'success',
  consumption: 'info',
  adjustment: 'amended',
  wastage: 'warning',
  return_to_supplier: 'neutral',
};

export const stockMovementColumns: Column<StockMovementRow>[] = [
  {
    key: 'occurred_at',
    header: 'When',
    width: '170px',
    frozen: true,
    render: (row) => formatDateTime(row.occurred_at),
  },
  {
    key: 'item',
    header: 'Reagent',
    render: (row) => (
      <span>
        <strong>{row.item_name}</strong>
        <span className="c-inventory__code ident">{row.lot_no}</span>
      </span>
    ),
  },
  {
    key: 'kind',
    header: 'Movement',
    width: '150px',
    render: (row) => (
      <Badge tone={MOVEMENT_TONES[row.kind]}>{ENUM_LABELS.stockMovementKind[row.kind]}</Badge>
    ),
  },
  {
    key: 'quantity',
    header: 'Quantity',
    width: '150px',
    align: 'end',
    render: (row) => (
      // Always signed, so the direction is readable without the badge.
      <span className={signedClass(row.quantity_base)}>
        {formatNumber(row.quantity_base, { signed: true })} {ENUM_LABELS.stockUnit[row.base_unit]}
      </span>
    ),
  },
  {
    key: 'why',
    header: 'For',
    render: (row) => row.report_ref ?? row.reason ?? <span className="text-fg-muted">—</span>,
  },
  { key: 'by', header: 'By', width: '160px', render: (row) => row.performed_by },
];
